import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { Trophy, Users, Plus, KeyRound, Sparkles, ArrowRight } from 'lucide-react';
import { Button, Input, Modal, DarkModeToggleCompact } from '../../components/ui';

export const WkDashboard = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [leagues, setLeagues] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showJoinModal, setShowJoinModal] = useState(false);
    
    // Form state
    const [newLeagueName, setNewLeagueName] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [error, setError] = useState('');
    
    // Stats State
    const [totalPoints, setTotalPoints] = useState(0);
    const [pendingPredictions, setPendingPredictions] = useState(0);

    useEffect(() => {
        if (user) {
            fetchMyLeagues();
            fetchStats();
        }
    }, [user]);

    const fetchStats = async () => {
        try {
            // 1. Get Total Points awarded to user
            const { data: predData } = await supabase
                .from('wk_predictions')
                .select('points_awarded')
                .eq('user_id', user.id);
            
            const sum = predData?.reduce((acc, curr) => acc + (curr.points_awarded || 0), 0) || 0;
            setTotalPoints(sum);

            // 2. Calculate how many matches user hasn't predicted yet
            // First count total valid group stage matches
            const { count: totalMatchCount } = await supabase
                .from('wk_matches')
                .select('*', { count: 'exact', head: true });
            
            const predictedCount = predData?.length || 0;
            setPendingPredictions(Math.max(0, (totalMatchCount || 0) - predictedCount));
        } catch (err) {
            console.error('Stats fetch err', err);
        }
    }

    const fetchMyLeagues = async () => {
        try {
            setLoading(true);
            // Fetch leagues where the user is a member
            const { data, error } = await supabase
                .from('wk_league_members')
                .select(`
                    league_id,
                    wk_leagues (
                        id,
                        name,
                        admin_id,
                        invite_code
                    )
                `)
                .eq('user_id', user.id);

            if (error) throw error;
            
            // Format data
            const formattedLeagues = data.map(member => member.wk_leagues);
            setLeagues(formattedLeagues);
        } catch (err) {
            console.error('Error fetching leagues:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateLeague = async (e) => {
        e.preventDefault();
        setError('');
        try {
            // 1. Create league
            const { data: league, error: createError } = await supabase
                .from('wk_leagues')
                .insert([{ name: newLeagueName, admin_id: user.id }])
                .select()
                .single();

            if (createError) throw createError;

            // 2. Add creator as member
            const { error: memberError } = await supabase
                .from('wk_league_members')
                .insert([{ league_id: league.id, user_id: user.id }]);

            if (memberError) throw memberError;

            setShowCreateModal(false);
            setNewLeagueName('');
            fetchMyLeagues();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleJoinLeague = async (e) => {
        e.preventDefault();
        setError('');
        try {
            // 1. Find league by code
            const { data: league, error: searchError } = await supabase
                .from('wk_leagues')
                .select('id')
                .eq('invite_code', inviteCode.trim())
                .single();

            if (searchError || !league) {
                throw new Error('Poule niet gevonden met deze code.');
            }

            // 2. Join league
            const { error: joinError } = await supabase
                .from('wk_league_members')
                .insert([{ league_id: league.id, user_id: user.id }]);

            // If user is already a member, supabase will throw a unique constraint error
            if (joinError && joinError.code !== '23505') throw joinError;

            setShowJoinModal(false);
            setInviteCode('');
            fetchMyLeagues();
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div className="min-h-screen bg-black text-white relative">
            {/* Ambient Background */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-[100px] pointer-events-none"></div>

            {/* Header */}
            <nav className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                        <img src="/logo-icon.webp" alt="ProPoule" className="h-8 w-8" />
                        <span className="font-black text-2xl tracking-tighter uppercase italic select-none">
                            <span>PRO</span><span className="text-orange-500">POULE</span>
                            <span className="ml-2 text-sm text-pink-500 tracking-normal font-bold">WK 2026</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <DarkModeToggleCompact />
                        <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="hidden sm:flex text-gray-400">
                            Naar Toernooi Planner
                        </Button>
                    </div>
                </div>
            </nav>

            <main className="max-w-5xl mx-auto px-6 py-12">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                    <div>
                        <h1 className="text-4xl font-black mb-2">{t('wk_poule.dashboard_title')}</h1>
                        <p className="text-gray-400">Welkom terug! Beheer hier je voorspellingen en groepen.</p>
                    </div>
                    <div className="flex gap-3">
                        <Button 
                            variant="outline" 
                            className="border-white/20 hover:bg-white/5"
                            onClick={() => setShowJoinModal(true)}
                            icon={<KeyRound size={18} />}
                        >
                            {t('wk_poule.btn_join_league')}
                        </Button>
                        <Button 
                            variant="primary" 
                            className="bg-gradient-to-r from-orange-500 to-pink-500 border-0"
                            onClick={() => setShowCreateModal(true)}
                            icon={<Plus size={18} />}
                        >
                            {t('wk_poule.btn_create_league')}
                        </Button>
                    </div>
                </div>

                {/* Summary Stats Bar */}
                {!loading && leagues.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 animate-slideUp">
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex items-center gap-4 relative overflow-hidden group">
                            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="p-4 bg-orange-500/20 rounded-xl text-orange-500">
                                <Trophy size={32} fill="currentColor" />
                            </div>
                            <div>
                                <div className="text-3xl font-black text-white">{totalPoints}</div>
                                <div className="text-sm text-gray-400 font-bold uppercase tracking-wider">Jouw Totaalscore</div>
                            </div>
                        </div>
                        
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex items-center gap-4 relative overflow-hidden group">
                            <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="p-4 bg-pink-500/20 rounded-xl text-pink-500">
                                <Sparkles size={32} />
                            </div>
                            <div>
                                <div className="text-3xl font-black text-white">{pendingPredictions}</div>
                                <div className="text-sm text-gray-400 font-bold uppercase tracking-wider">Nog in te vullen</div>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/10 border-t-orange-500"></div>
                    </div>
                ) : leagues.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-3xl p-12 text-center flex flex-col items-center">
                        <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mb-6">
                            <Trophy className="text-orange-500" size={40} />
                        </div>
                        <h3 className="text-2xl font-bold mb-2">Nog geen poules</h3>
                        <p className="text-gray-400 max-w-md mx-auto mb-8">
                            Je doet nog niet mee aan een WK Poule. Maak er zelf één aan voor je vrienden of voer een invite code in.
                        </p>
                        <Button 
                            variant="primary" 
                            size="lg"
                            className="bg-orange-500 text-white"
                            onClick={() => setShowCreateModal(true)}
                        >
                            Start je eerste Poule
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {leagues.map(league => (
                            <div 
                                key={league.id} 
                                onClick={() => navigate(`/wkpoule/league/${league.id}`)}
                                className="bg-white/5 border border-white/10 hover:border-orange-500/50 rounded-2xl p-6 cursor-pointer transition-all hover:bg-white/10 group"
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-white/10 rounded-xl group-hover:bg-orange-500/20 transition-colors">
                                        <Users className="text-white group-hover:text-orange-500" size={24} />
                                    </div>
                                    {league.admin_id === user.id && (
                                        <span className="text-xs font-bold px-2 py-1 bg-blue-500/20 text-blue-400 rounded-md">Admin</span>
                                    )}
                                </div>
                                <h3 className="text-xl font-bold mb-1">{league.name}</h3>
                                <p className="text-sm text-gray-500 flex items-center gap-2 mb-6">
                                    Invite Code: <span className="font-mono text-gray-300 bg-black/50 px-2 py-0.5 rounded">{league.invite_code}</span>
                                </p>
                                <div className="flex items-center text-orange-500 text-sm font-bold group-hover:translate-x-2 transition-transform">
                                    Bekijk Stand <ArrowRight size={16} className="ml-1" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* === CROSS-SELL VIRAL CTA === */}
                <div className="mt-16 border-t border-white/10 pt-10">
                    <div className="bg-gradient-to-r from-orange-500/10 to-pink-500/10 border border-orange-500/20 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl -z-10"></div>
                        <div className="text-center md:text-left">
                            <h3 className="text-2xl font-black text-white mb-2 flex items-center justify-center md:justify-start gap-2">
                                <Sparkles className="text-orange-500" size={24} />
                                Organiseer je ook Clubtoernooien?
                            </h3>
                            <p className="text-gray-400 max-w-xl">
                                Maak moeiteloos professionele schema's voor Voetbal, Padel of Tennis. 
                                Gebruik de officiële <span className="text-white font-bold">ProPoule Planner</span> en breng je club naar het volgende niveau!
                            </p>
                        </div>
                        <Button 
                            size="lg"
                            onClick={() => navigate('/admin/dashboard')}
                            className="bg-white text-black hover:bg-gray-100 font-black rounded-xl shadow-xl shadow-white/10 whitespace-nowrap transition-all hover:scale-105"
                        >
                            Ontdek Toernooi Planner
                        </Button>
                    </div>
                </div>
            </main>

            {/* Create Modal */}
            <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Nieuwe Poule Maken">
                <form onSubmit={handleCreateLeague} className="space-y-6">
                    <p className="text-gray-400 text-sm">Geef je poule een herkenbare naam. Je kunt later vrienden uitnodigen via de unieke invite code.</p>
                    <Input
                        label="Naam van de poule"
                        value={newLeagueName}
                        onChange={(e) => setNewLeagueName(e.target.value)}
                        placeholder="Bijv. Familie de Vries"
                        required
                    />
                    {error && <p className="text-red-500 text-sm bg-red-500/10 p-3 rounded-lg">{error}</p>}
                    <Button type="submit" variant="primary" className="w-full bg-orange-500">Aanmaken</Button>
                </form>
            </Modal>

            {/* Join Modal */}
            <Modal isOpen={showJoinModal} onClose={() => setShowJoinModal(false)} title="Word Lid van een Poule">
                <form onSubmit={handleJoinLeague} className="space-y-6">
                    <p className="text-gray-400 text-sm">Heb je een invite code gekregen via WhatsApp of e-mail? Vul hem hieronder in.</p>
                    <Input
                        label="Invite Code"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value)}
                        placeholder="bijv. a1b2c3d4"
                        required
                    />
                    {error && <p className="text-red-500 text-sm bg-red-500/10 p-3 rounded-lg">{error}</p>}
                    <Button type="submit" variant="primary" className="w-full bg-orange-500">Lid Worden</Button>
                </form>
            </Modal>
        </div>
    );
};
